<script setup lang="ts">
/// <reference types="@types/google.maps" />
import { useShopwareContext } from '@shopware/composables'
import StoreFinderStore from './StoreFinderStore.vue'
import type { Store } from './StoreFinderStore.vue'
import type { Place } from '~/composables/usePlaceAutocomplete'
import type { Schemas } from '#shopware'

interface StoreMarker {
    order: number
    position: {
        lat: number
        lng: number
    }
    infoWindow: {
        component: typeof StoreFinderStore
        props: {
            store: Store
        }
    }
    onClick(): void
}

const { t } = useI18n()
const { apiClient } = useShopwareContext()
const { onUcIsInitialized, getUcServiceStatus, onUcConsentChange, acceptUcServiceStatus } = useUcConsentManager()
const { initPlacesLibrary, initAutocomplete, place } = usePlaceAutocomplete()

const stores = shallowRef<StoreMarker[]>([])
const mapsLoaded = ref(false)
const selectedMarker = ref<number | undefined>()
const storeItemContainer = ref<HTMLElement | null>(null)
const fittingAddressInput = ref<HTMLInputElement | null>(null)
const consentGiven = ref(false)
const mounted = ref(false)
const selectedRadius = ref(0)
const selectedCountry = ref('')
const selectedLocation = ref<{ lat: number | null; lng: number | null }>({ lat: null, lng: null })

const countries = ref([
    { id: '', name: t('fitting.countrySelectOptionAll') },
    { id: '409343cdae124668bd5ed08eb3442893', name: t('fitting.countryGermany') },
    { id: '23f1e3a2eba64c5197e47e7e5e41e785', name: t('fitting.countrySwiss') },
])

const rangeDeactivated = computed(() => {
    return selectedLocation.value.lat === 0 && selectedLocation.value.lng === 0
})

onMounted(() => {
    onUcIsInitialized(handleGoogleMapsConsent)
    mounted.value = true
})

watch(mapsLoaded, (newVal) => {
    if (newVal == null) return
    if (consentGiven.value) {
        onSubmit()
    }
})

watch(place, (newVal) => {
    if (newVal == null) return

    const placeResult = newVal as google.maps.places.PlaceResult
    selectedLocation.value = {
        lat: placeResult.geometry?.location?.lat() ?? null,
        lng: placeResult.geometry?.location?.lng() ?? null,
    }

    onSubmit()
})

function onStoreClick(i: number) {
    selectedMarker.value = i
    borderActiveItem(i)
}

function borderActiveItem(i: number) {
    const children = storeItemContainer.value?.children
    if (children == null) return
    Array.from(children).forEach(child => child.classList.remove('border-primary'))
    children[i]?.classList.add('border-primary')
}

function highlightItem(i: number) {
    borderActiveItem(i)
    const item = storeItemContainer.value?.children[i]
    if (item == null) return

    item.classList.add('bg-base-300')
    setTimeout(() => {
        item.classList.remove('bg-base-300')
    }, 300)

    item.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

async function handleGoogleMapsConsent() {
    const ucServiceName = 'Google Maps'

    if (getUcServiceStatus(ucServiceName)) {
        consentGiven.value = true
        await bindAutoComplete(fittingAddressInput)
    }

    onUcConsentChange(async (e: CustomEvent) => {
        if (e.detail && e.detail.event === 'consent_status') {
            if (e.detail[ucServiceName] === true && !window.google?.maps?.places) {
                consentGiven.value = true
                await bindAutoComplete(fittingAddressInput)
            } else if (e.detail[ucServiceName] === false && window.google?.maps?.places) {
                location.reload()
            }
        }
    })
}

function onClickAcceptService() {
    acceptUcServiceStatus('S1pcEj_jZX')
}

async function bindAutoComplete(input: typeof fittingAddressInput) {
    await initPlacesLibrary()
    if (input.value != null) {
        initAutocomplete(input.value, ['geocode'])
    }
}

async function onSubmit() {
    try {
        const { data } = await apiClient.invoke('searchFittingCenters post /a4g-fitting-center/search', {
            body: {
                radius: selectedRadius.value,
                latitude: selectedLocation.value.lat,
                longitude: selectedLocation.value.lng,
                countryId: selectedCountry.value,
            },
        })

        if (data.elements == null || Object.keys(data.elements).length < 1) return

        stores.value = mapStores(Object.values(data.elements)).sort((a, b) => a.order - b.order)
    } catch (e) {
        console.error(e)
    }
}

function mapStores(elements: Schemas['FittingCenterElement'][]): StoreMarker[] {
    return elements.map((el, key) => {
        return {
            order: el.position,
            position: {
                lat: el.latitude,
                lng: el.longitude,
            },
            infoWindow: {
                component: StoreFinderStore,
                props: {
                    store: {
                        type: el.internal ? 'internal' : 'external',
                        name: el.name,
                        street: el.street,
                        zipcode: el.zipcode,
                        city: el.city,
                        country: el.country?.translated?.name,
                        image: el.media != null ? { url: el.media.url, alt: el.media.translated?.alt } : null,
                        fittingOptions: el.options?.map(option => ({
                            name: option.translated?.name,
                            description: option.translated?.description,
                            link: option.translated?.link,
                            linkText: option.translated?.linkText,
                        })) ?? [],
                        manufacturers: el.manufacturers?.map(manufacturer => ({
                            id: manufacturer.id,
                            media: manufacturer.media != null
                                ? { url: manufacturer.media.url, alt: manufacturer.media.translated?.alt }
                                : null,
                            link: manufacturer.extensions?.a4g_brand?.path ?? null,
                        })) ?? [],
                        fitters: el.contacts?.map(contact => ({
                            image: contact.media != null
                                ? { url: contact.media.url, alt: contact.media.translated?.alt }
                                : null,
                            name: contact.name,
                            phone: contact.telephone,
                            email: contact.email,
                            url: contact.translated?.link,
                        })) ?? [],
                        showTitleInModal: el.showTitleInModal,
                        showModalSubtitle: el.showModalSubtitle,
                        modalSubtitle: el.modalSubtitle ?? null,
                    },
                },
            },
            onClick: () => {
                highlightItem(key)
            },
        }
    })
}

async function onAddressInputEnter() {
    const suggestionSelected = document.querySelectorAll('.pac-item-selected').length > 0

    if (!suggestionSelected) {
        const firstResult = document.querySelector('.pac-container .pac-item:first-child')?.textContent

        if (firstResult == null) return

        const geocoder = new google.maps.Geocoder()
        geocoder.geocode({ address: firstResult }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results?.[0] != null) {
                place.value = results[0] as Place

                if (fittingAddressInput.value != null) {
                    fittingAddressInput.value.value = results[0].address_components?.[0]?.long_name ?? ''
                }
            }
        })
    }
}

async function onCountryChange() {
    resetRadius()
    resetSelectedLocation()
    await onSubmit()
}

function resetRadius() {
    selectedRadius.value = 0
}

function resetSelectedLocation() {
    if (fittingAddressInput.value != null) {
        fittingAddressInput.value.value = ''
    }
    selectedLocation.value = { lat: null, lng: null }
}

function onClickSearchButton() {
    if (fittingAddressInput.value == null || fittingAddressInput.value.value.trim() === '') return

    const event = new KeyboardEvent('keydown', {
        key: 'Enter',
        code: 'Enter',
        which: 13,
        keyCode: 13,
    })

    fittingAddressInput.value.dispatchEvent(event)
}
</script>

<template>
    <div class="relative flex flex-col gap-4">
        <form class="flex justify-start items-center gap-4">
            <div class="form-control hidden">
                <label
                    for="fittingCountry"
                    class="sr-only"
                >{{ $t('fitting.countrySelectLabel') }}</label>
                <select
                    id="fittingCountry"
                    v-model="selectedCountry"
                    class="select select-bordered text-sm"
                    @change="onCountryChange()"
                >
                    <option
                        v-for="(country, countryIndex) in countries"
                        :key="countryIndex"
                        :value="country.id"
                    >
                        {{ country.name }}
                    </option>
                </select>
            </div>
            <div class="form-control w-full">
                <label
                    for="fittingAddressInput"
                    class="sr-only"
                >{{ $t('fitting.addressInputPlaceholder') }}</label>
                <div class="join">
                    <input
                        id="fittingAddressInput"
                        ref="fittingAddressInput"
                        type="text"
                        class="input input-bordered w-full text-sm join-item"
                        :placeholder="$t('fitting.addressInputPlaceholder')"
                        @keydown.enter.prevent="onAddressInputEnter()"
                    >
                    <button
                        type="button"
                        class="btn btn-accent join-item"
                        @click="onClickSearchButton()"
                    >
                        <MiscIcon name="search" />
                    </button>
                </div>
            </div>
            <div class="form-control">
                <label
                    for="fittingRadius"
                    class="sr-only"
                >{{ $t('fitting.radiusLabel') }}</label>
                <select
                    id="fittingRadius"
                    v-model="selectedRadius"
                    class="select select-bordered text-sm"
                    :disabled="rangeDeactivated"
                    @change="onSubmit()"
                >
                    <option :value="0">
                        {{ $t('fitting.radiusOptionNone') }}
                    </option>
                    <option :value="25">
                        25 km
                    </option>
                    <option :value="50">
                        50 km
                    </option>
                    <option :value="100">
                        100 km
                    </option>
                    <option :value="250">
                        250 km
                    </option>
                    <option :value="500">
                        500 km
                    </option>
                </select>
            </div>
        </form>

        <div class="flex flex-col gap-5 w-full xl:flex-row xl:min-h-[500px]">
            <div
                v-if="stores.length > 0"
                ref="storeItemContainer"
                class="order-2 flex flex-col gap-6 max-h-[600px] overflow-y-scroll min-w-[320px] lg:pr-2 xl:w-[540px] xl:order-1 xl:max-h-[795px]"
            >
                <StoreFinderStore
                    v-for="(store, storeIndex) in stores"
                    :key="storeIndex"
                    :store="store.infoWindow.props.store"
                    class="transition-colors cursor-pointer border"
                    @click="onStoreClick(storeIndex)"
                />
            </div>
            <MiscGoogleMap
                v-if="consentGiven"
                :markers="stores"
                :select-marker="selectedMarker"
                class="h-[280px] order-1 w-full md:h-[486px] lg:h-[780px] xl:h-[795px] xl:order-2"
                @loaded="mapsLoaded = $event"
            />
        </div>

        <div
            v-if="!consentGiven && mounted"
            class="absolute top-0 left-0 w-full h-full flex justify-center items-center z-50"
        >
            <div class="absolute top-0 left-0 w-full h-full bg-base-300 opacity-80" />
            <div class="z-10 flex flex-col items-center gap-3">
                <div>{{ $t('misc.cookieConsent.contentHidden', { name: 'Google Maps' }) }}</div>
                <button
                    type="button"
                    class="btn btn-primary"
                    @click="onClickAcceptService()"
                >
                    {{ $t('misc.cookieConsent.allowConsent') }}
                </button>
            </div>
        </div>
    </div>
</template>
